import {Card, Label, Preview, PreviewAction} from 'platform/components';
import {Box, Grid, GridItem, Heading, makeStorageUrl, Show, VStack} from 'platform/foundation';
import {Lightbox, useLightbox, LightboxImage} from 'platform/lightbox';

import {isNotNil} from 'ramda';
import {concatRight, isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  VideoPreview,
  downloadFile,
  getPrecedingPlayableVideos,
  isVideoPlayable,
  useDmsLightboxActionsFactory,
} from '@dms/shared';

import {buildArray, suffixTestId, TestIdProps} from 'shared/web';

import {AssetsChildCategory, AssetCategoryItemFile} from '../utils/types';

interface AuditPhotosCategoryProps extends TestIdProps {
  category: AssetsChildCategory;
}

export function AuditPhotosCategory({category, ...rest}: AuditPhotosCategoryProps) {
  const assets = category.items.reduce((results: AssetCategoryItemFile[], categoryPhotoItem) => {
    const files = categoryPhotoItem.files.map((file) => file);
    return concatRight(results, files);
  }, []);

  const [lightboxControls, {onOpen}] = useLightbox(`${category.id}-apc`);
  const lightboxActionsFactory = useDmsLightboxActionsFactory();

  const lightboxImages = lightboxActionsFactory.addFullActions(
    assets
      .filter((asset) => !asset.isVideo)
      .reduce((lightboxImages: LightboxImage[], image) => {
        const imageUrl = image.url || image.resizeUrl;
        if (isNotNil(imageUrl)) {
          return [
            ...lightboxImages,
            {
              id: image.id,
              url: imageUrl.replace('resize', 'get'),
              resizeUrl: image.resizeUrl,
              title: image.name,
              fileName: image.name,
            },
          ];
        }
        return lightboxImages;
      }, [])
  );
  const lightboxVideos =
    lightboxActionsFactory.addShareOnlyActions(
      assets.filter((asset) => asset.isVideo),
      (video) => [
        {
          id: 'downloadBtn',
          icon: 'file/download',
          onClick: () => {
            if (isNotNil(video.originUrl)) {
              downloadFile(video.originUrl);
            }
          },
        },
      ]
    ) ?? [];

  const handleOpenPreview = (imageOriginalUrl: string | null) => {
    const detailIndex = lightboxImages?.findIndex((image) => image.url === imageOriginalUrl);
    onOpen(detailIndex ?? 0);
    return;
  };

  return (
    <>
      <Card variant="inlineGrey" title={category.name}>
        <Grid columns={6}>
          {category.items.map((categoryPhotoItem) => (
            <GridItem
              key={`${categoryPhotoItem.label}_${category.id}`}
              span={
                isNotNilOrEmpty(categoryPhotoItem?.label) && isNilOrEmpty(categoryPhotoItem.title)
                  ? 1
                  : 6
              }
            >
              <VStack spacing={2}>
                <Box>
                  <Show when={isNotNilOrEmpty(categoryPhotoItem?.label)}>
                    <Label>{categoryPhotoItem.label}</Label>
                  </Show>
                  <Show when={isNotNilOrEmpty(categoryPhotoItem?.title)}>
                    <Heading
                      size={4}
                      data-testid={suffixTestId(`category-title-${categoryPhotoItem.title}`, rest)}
                    >
                      {categoryPhotoItem.title}
                    </Heading>
                  </Show>
                </Box>

                <Grid
                  columns={
                    isNotNilOrEmpty(categoryPhotoItem?.label) &&
                    isNilOrEmpty(categoryPhotoItem.title)
                      ? 1
                      : 6
                  }
                >
                  {categoryPhotoItem?.files.map((file, index) => {
                    if (file.isVideo) {
                      return (
                        <VideoPreview
                          key={file.id}
                          videoAsset={file}
                          ratio="4/3"
                          isLoading={!isVideoPlayable(file)}
                          onOpenPreview={() =>
                            onOpen(
                              getPrecedingPlayableVideos(lightboxVideos, index) +
                                (lightboxImages?.length ?? 0)
                            )
                          }
                          data-testid={suffixTestId('auditVideo', rest)}
                        />
                      );
                    }

                    return (
                      <Box
                        key={`${file.damageId ?? file.id}-${index}`} // eslint-disable-line react/no-array-index-key -- Pre-existing violation
                        width="100%"
                        height="100%"
                        ratio="4 / 3"
                        overflow="hidden"
                        position="relative"
                        borderRadius="small"
                      >
                        <Preview
                          url={file.resizeUrl}
                          makeUrl={makeStorageUrl}
                          width="100%"
                          height="100%"
                          fit="cover"
                          actions={buildArray<PreviewAction>().add({
                            icon: 'action/visibility',
                            onClick: () => handleOpenPreview(file.url),
                          })}
                          data-testid={suffixTestId(`category-auditPhotoItem-[${index}]`, rest)}
                        />
                      </Box>
                    );
                  })}
                </Grid>
              </VStack>
            </GridItem>
          ))}
        </Grid>
      </Card>
      <Lightbox
        data-testid={suffixTestId('category', rest)}
        controls={lightboxControls}
        images={lightboxImages}
        videos={lightboxVideos}
      />
      {lightboxActionsFactory.photoDownloadSizeDialog}
    </>
  );
}
